import React, { useEffect, useState, useRef } from "react";
import FormField from "./FormField";
import DateRangePicker from "./DateRangePicker";
import { X } from "lucide-react";
import { Button } from "../ui/button";

export interface ICommonOption {
    value: string | number;
    label: string;
}

export interface IFilter {
    filter?: string;
    sort?: string;
    [key: string]: any;
}

interface CommonFilterProps {
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    value?: IFilter;
    filterOptions?: ICommonOption[];
    sortOptions?: ICommonOption[];
    onChange?: (value: string, name: string) => void;
    searchPlaceholder?: string;
    filterPlaceholder?: string;
    sortPlaceholder?: string;
    filterName?: string;
    sortName?: string;
    startDate?: string;
    endDate?: string;
    onDateRangeChange?: (startDate: string, endDate: string) => void;
}

const CommonFilter: React.FC<CommonFilterProps> = ({
    searchValue = "",
    onSearchChange = (search) => console.log('search', search),
    value,
    filterOptions = [],
    sortOptions = [],
    onChange,
    searchPlaceholder = "Search...",
    filterPlaceholder = "Filter",
    sortPlaceholder = "Sort By",
    filterName = "filter",
    sortName = "sort",
    startDate = "",
    endDate = "",
    onDateRangeChange,
}) => {
    const [search, setSearch] = useState(searchValue);

    const onSearchChangeRef = useRef(onSearchChange);
    useEffect(() => {
        onSearchChangeRef.current = onSearchChange;
    }, [onSearchChange]);

    useEffect(() => {
        setSearch(searchValue || "");
    }, [searchValue]);

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const timer = setTimeout(() => {
            onSearchChangeRef.current?.(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    return (
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center my-3 pt-3">
            {/* Search */}
            {onSearchChange !== undefined && (
                <div className="relative flex-1 max-w-md">
                    <FormField
                        type="search"
                        placeholder={searchPlaceholder}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            )}

            {/* Filter & Sort */}
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-end">
                {/* Date Range Picker */}
                {onDateRangeChange !== undefined && (
                    <div className="flex items-center gap-2 relative z-30 w-full sm:w-auto">
                        <div className="w-full sm:w-64">
                            <DateRangePicker
                                startDate={startDate}
                                endDate={endDate}
                                onChange={onDateRangeChange}
                                placeholder="Filter by Date Range"
                            />
                        </div>
                        {(startDate || endDate) && (
                            <Button
                                variant="outline"
                                size="icon-lg"
                                className="bg-white"
                                type="button"
                                onClick={() => onDateRangeChange("", "")}
                                title="Clear Date Range"
                            >
                                <X size={16} />
                            </Button>
                        )}
                    </div>
                )
                }
                {filterOptions.length > 0 && (
                    <div className="w-full sm:w-48">
                        <FormField
                            type="select"
                            name={filterName}
                            placeholder={filterPlaceholder}
                            value={value?.[filterName] || ""}
                            onChange={(e) => onChange?.(e.target.value, filterName)}
                            options={filterOptions.map(opt => ({
                                label: opt.label,
                                value: String(opt.value)
                            }))}
                        />
                    </div>
                )}

                {sortOptions.length > 0 && (
                    <div className="w-full sm:w-48">
                        <FormField
                            type="select"
                            name={sortName}
                            placeholder={sortPlaceholder}
                            value={value?.[sortName] || ""}
                            onChange={(e) => onChange?.(e.target.value, sortName)}
                            options={sortOptions.map(opt => ({
                                label: opt.label,
                                value: String(opt.value)
                            }))}
                        />
                    </div>
                )}
            </div>
        </div >
    );
};

export default CommonFilter;